/**
 * Grouped vertical bars.
 *
 * ⚠ A faithful port of `CS.charts.groupedBars` from the validated mockups — same
 * geometry, same paddings, same label rules. Ubi signed off on those screens, so this
 * is a translation into React, not a redesign. Hand-rolled SVG for the same reason the
 * mockups are: no charting library, and nothing animates, because the motion rules
 * forbid animating data.
 *
 * The click behaviour, the faded rows and `groupTotals` are ADDED, the way HBars added its
 * own (28 Sep 2026, the in-and-out chart): without those props every bar renders exactly
 * as it always did.
 */

import { formatNumber } from "@quality-field/shared";

export const CHART_PALETTE: Record<string, string> = {
  circet: "#84257a",
  constructel: "#ed125f",
  total: "#f47b3b",
  emerald: "#10b981",
  blue: "#3b82f6",
  amber: "#f59e0b",
  orange: "#f97316",
  red: "#ef4444",
  slate: "#94a3b8",
};

/** Round an axis maximum up to something a person would choose. */
function niceMax(value: number): number {
  if (value <= 0) return 10;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  let nice = Math.ceil(value / magnitude) * magnitude;
  if (nice / value > 1.6) nice = (Math.ceil((value / magnitude) * 2) / 2) * magnitude;
  return nice;
}

export interface BarRow {
  label: string;
  /** What a click reports for this row. The label when absent. */
  id?: string;
  /** A row may carry its own colour, which wins over the series colour. */
  color?: string;
  /** Drawn faded — a period that is not over yet. */
  muted?: boolean;
  [key: string]: string | number | boolean | undefined;
}

export interface BarSeries {
  key: string;
  label: string;
  color: string;
}

export interface GroupedBarsProps {
  rows: BarRow[];
  series: BarSeries[];
  width?: number;
  height?: number;
  rotateLabels?: boolean;
  /** Replaces the series legend, for charts whose rows carry their own colours. */
  legend?: Array<{ label: string; color: string }>;
  /**
   * Clicking a bar calls this with its row id and series key. ⚠ A bar of zero is never
   * clickable: it is drawn as nothing, and an option that empties the screen is a trap.
   */
  onSelect?: (rowId: string, seriesKey: string) => void;
  /** The bar currently chosen; the others fade rather than disappear. */
  selected?: { rowId: string; seriesKey: string } | null;
  /**
   * ⚠ False for series that do not add up. Where the bars are too narrow for their own
   * values, the mockups label the group with the SUM of its bars — meaningless when the
   * series are "in", "done" and "validated". The width should then leave room for per-bar
   * values; the tooltip carries each one either way.
   */
  groupTotals?: boolean;
  /** One bar's tooltip, which is also its screen-reader text. `label · series: n` by default. */
  titleOf?: (row: BarRow, series: BarSeries, value: number) => string;
}

export function GroupedBars({
  rows,
  series,
  width = 640,
  height = 220,
  rotateLabels = false,
  legend,
  onSelect,
  selected = null,
  groupTotals = true,
  titleOf,
}: GroupedBarsProps) {
  // ⚠ The viewBox aspect IS the rendered aspect: `w-full` plus a fixed pixel height
  // letterboxes the drawing and leaves fat margins left and right. A wide card passes a
  // wide `width` so the two stay roughly in proportion.
  const padL = 42;
  const padR = 8;
  const padB = rotateLabels ? 58 : 34;
  const padT = 22; // headroom for the value labels
  const plotW = width - padL - padR;
  const plotH = height - padB - padT;

  const value = (row: BarRow, key: string): number => Number(row[key] ?? 0);

  let max = 0;
  for (const row of rows) for (const s of series) max = Math.max(max, value(row, s.key));
  max = niceMax(max);

  const groupW = rows.length ? plotW / rows.length : plotW;
  const barW = Math.min(18, (groupW - 10) / series.length);

  // Widest number we will have to draw, at ~5.6px per digit for the 9px label. Where
  // the bars are too narrow to hold a legible number the GROUP TOTAL is labelled
  // instead: a label that collides with its neighbour is worse than no label at all.
  let widest = 0;
  for (const row of rows) for (const s of series) widest = Math.max(widest, String(value(row, s.key)).length);
  const perBarLabels = barW >= widest * 5.6;

  const ticks = 4;
  const legendItems = legend ?? series.map((s) => ({ label: s.label, color: s.color }));

  return (
    <>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height: `${height}px` }} role="img">
        <g className="chart-grid">
          {Array.from({ length: ticks + 1 }, (_, i) => {
            const y = padT + plotH - (plotH * i) / ticks;
            return (
              <g key={i}>
                <line x1={padL} y1={y} x2={width - padR} y2={y} />
                <text className="chart-axis" x={padL - 8} y={y + 3} textAnchor="end">
                  {Math.round((max * i) / ticks)}
                </text>
              </g>
            );
          })}
        </g>

        {rows.map((row, gi) => {
          const gx = padL + gi * groupW;
          const totalW = barW * series.length + 3 * (series.length - 1);
          const startX = gx + (groupW - totalW) / 2;
          let groupTotal = 0;
          let groupTop = padT + plotH;

          const rowId = row.id ?? row.label;

          const bars = series.map((s, si) => {
            const v = value(row, s.key);
            const h = max ? (v / max) * plotH : 0;
            const x = startX + si * (barW + 3);
            const y = padT + plotH - h;
            groupTotal += v;
            groupTop = Math.min(groupTop, y);

            const isSelected = selected !== null && selected.rowId === rowId && selected.seriesKey === s.key;
            const clickable = onSelect !== undefined && v > 0;
            const choose = () => onSelect?.(rowId, s.key);
            const opacity = selected !== null && !isSelected ? 0.35 : row.muted ? 0.55 : 1;

            return (
              <g
                className={clickable ? "bar cursor-pointer" : "bar"}
                key={s.key}
                opacity={opacity}
                {...(clickable
                  ? {
                      role: "button",
                      tabIndex: 0,
                      "aria-pressed": isSelected,
                      onClick: choose,
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          choose();
                        }
                      },
                    }
                  : {})}
              >
                {/* The <title> is the hover tooltip and the screen-reader text in one. */}
                <title>{titleOf ? titleOf(row, s, v) : `${row.label} · ${s.label}: ${formatNumber(v)}`}</title>
                {/* A clickable bar is hit over its whole column: a bar of one punch is two
                    pixels high, and the target has to be bigger than the mark. */}
                {clickable && <rect x={x - 1} y={padT} width={barW + 2} height={plotH} fill="transparent" />}
                <rect
                  x={x}
                  y={y}
                  width={barW}
                  height={Math.max(h, v > 0 ? 2 : 0)}
                  rx={2}
                  fill={row.color ?? s.color}
                  stroke={isSelected ? "#0f172a" : undefined}
                  strokeWidth={isSelected ? 1.5 : undefined}
                />
                {perBarLabels && v > 0 && (
                  <text className="chart-val" x={x + barW / 2} y={y - 5} textAnchor="middle">
                    {v}
                  </text>
                )}
              </g>
            );
          });

          const lx = gx + groupW / 2;
          const ly = height - padB + 16;

          return (
            <g key={`${row.label}-${gi}`}>
              {bars}
              {groupTotals && !perBarLabels && groupTotal > 0 && (
                <text className="chart-val" x={gx + groupW / 2} y={groupTop - 5} textAnchor="middle">
                  {groupTotal}
                </text>
              )}
              {rotateLabels ? (
                <text
                  className="chart-axis"
                  x={lx}
                  y={ly}
                  textAnchor="end"
                  transform={`rotate(-45 ${lx} ${ly})`}
                >
                  {row.label}
                </text>
              ) : (
                <text className="chart-axis" x={lx} y={ly} textAnchor="middle">
                  {row.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      <div className="mt-1 flex flex-wrap items-center gap-4">
        {legendItems.map((item) => (
          <span key={item.label} className="flex items-center gap-1.5 text-xs text-slate-500">
            <span style={{ width: 10, height: 10, borderRadius: 2, background: item.color }} />
            {item.label}
          </span>
        ))}
      </div>
    </>
  );
}
